import { Dispatch, FunctionComponent, SetStateAction, useState } from "react";
import { TransactionDetail } from "@type/core/transaction/transaction.types";
import { ActiveUser } from "@type/core/auth/auth.types";
import { Signatory } from "@type/core/dashboard/dashboard.types";
import CreditConditionalButton from "@components/core/buttons/CreditConditionalButton";
import { useTranslation } from "react-i18next";
import BasicModal from "@components/core/modals/BasicModal";
import { Right } from "@/type/core/offices/offices.types";

interface TransactionLaunchButtonProps {
  user?: ActiveUser;
  transaction: TransactionDetail;
  signatories: Signatory[];
  officeRights?: Right[];
  signatureRight: Right;
  scellementRight: Right;
  relaunch: boolean;
  launchTransaction: (relaunch?: boolean, direct?: boolean) => void;
  disabled: boolean;
  disabledAfterLaunch: boolean;
  setDisabledAfterLaunch: Dispatch<SetStateAction<boolean>>;
  setIsDirectSignatureModalOpen: Dispatch<SetStateAction<boolean>>;
  certificateDegradedMode: boolean;
  signatureDegradedMode: boolean;
  sealDegradedMode: boolean;
  className?: string;
  label?: string;
}

const TransactionLaunchButton: FunctionComponent<
  TransactionLaunchButtonProps
> = ({
  user,
  transaction,
  signatories,
  officeRights,
  signatureRight,
  scellementRight,
  relaunch,
  launchTransaction,
  disabled,
  disabledAfterLaunch,
  setDisabledAfterLaunch,
  setIsDirectSignatureModalOpen,
  certificateDegradedMode,
  signatureDegradedMode,
  sealDegradedMode,
  className = "",
  label,
}) => {
  const { t } = useTranslation();
  const [isCombinedSignatureModalOpen, setIsCombinedSignatureModalOpen] =
    useState<boolean>(false);

  const signatureType = transaction && transaction.type === "signature";
  const scellementType = transaction && transaction.type === "scellement";
  let hasSignatoryWithCertificate = false;
  if (signatureType) {
    hasSignatoryWithCertificate =
      transaction && transaction.signatories
        ? transaction.signatories.some((signatory) => signatory.withCertificate)
        : false;
  }
  if (
    (signatureType && officeRights?.includes(signatureRight)) ||
    (scellementType && officeRights?.includes(scellementRight))
  ) {
    let directSignature = false;
    if (
      signatureType &&
      officeRights.includes("SIGNATURE_COLLECTE_SIGNER") &&
      user?.certificateInfos
    ) {
      if (transaction && transaction.invitationMode === "parallel") {
        directSignature = signatories.some(
          (signatory) =>
            signatory.email === user?.email && signatory.status !== "signed",
        );
      } else {
        const firstSignatory = signatories.find((s) => s.rank === 1);
        directSignature =
          (firstSignatory && user?.email === firstSignatory.email) ?? false;
      }
    }

    const showCorrectLabel = (): string => {
      if (label) {
        return label;
      }
      if (relaunch) {
        return t("transaction.detail.relaunch");
      }

      return t("transaction.detail.send");
    };

    return (
      <>
        <CreditConditionalButton
          transaction={transaction}
          variant={relaunch ? "warning" : "primary"}
          label={showCorrectLabel()}
          disabled={
            disabled ||
            disabledAfterLaunch ||
            (signatureType &&
              (signatureDegradedMode ||
                (certificateDegradedMode && hasSignatoryWithCertificate))) ||
            (scellementType && sealDegradedMode)
          }
          onClick={() => {
            if (
              (signatureType &&
                (signatureDegradedMode ||
                  (certificateDegradedMode && hasSignatoryWithCertificate))) ||
              (scellementType && sealDegradedMode)
            ) {
              return;
            }
            if (transaction.signatureRequirementMode === "SOME") {
              setIsCombinedSignatureModalOpen(true);
            } else {
              setDisabledAfterLaunch(!disabledAfterLaunch);
              if (directSignature) {
                setIsDirectSignatureModalOpen(true);
              } else {
                launchTransaction(relaunch);
              }
            }
          }}
          className={`py-11 px-30 font-extra-small ${className}`}
        />
        <BasicModal
          visible={isCombinedSignatureModalOpen}
          onCancel={() => setIsCombinedSignatureModalOpen(false)}
          closable={false}
          title={t("signature.combinedSignatureLaunch.title")}
          buttonText={t("buttons.continue")}
          onOk={() => {
            setIsCombinedSignatureModalOpen(false);
            setDisabledAfterLaunch(!disabledAfterLaunch);
            if (directSignature) {
              setIsDirectSignatureModalOpen(true);
            } else {
              launchTransaction(relaunch);
            }
          }}
        >
          {t("signature.combinedSignatureLaunch.body")}
        </BasicModal>
      </>
    );
  } else return null;
};

export default TransactionLaunchButton;
